import React, { useState } from 'react';
import { AlertCircle, Copy, Check, ChevronDown, ChevronRight } from 'lucide-react';

interface FailureContentProps {
  message: string;
  type: string;
  retryable?: boolean;
  stackTrace?: string;
}

export const FailureContent: React.FC<FailureContentProps> = ({
  message,
  type,
  retryable,
  stackTrace,
}) => {
  const [copied, setCopied] = useState(false);
  const [showStackTrace, setShowStackTrace] = useState(false);

  const handleCopy = () => {
    const errorText = [
      `Error: ${message}`,
      `Type: ${type}`,
      retryable !== undefined ? `Retryable: ${retryable ? 'Yes' : 'No'}` : '',
      stackTrace ? `\nStack Trace:\n${stackTrace}` : '',
    ]
      .filter(Boolean)
      .join('\n');

    void navigator.clipboard.writeText(errorText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-6">
      <div className="rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-red-100 dark:bg-red-900/40 border-b border-red-200 dark:border-red-900/50">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
            <span className="font-medium text-red-700 dark:text-red-300">Step Failed</span>
          </div>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/60 rounded transition-colors"
            title="Copy error details"
          >
            {copied ? (
              <>
                <Check className="h-3 w-3" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-3 w-3" />
                Copy
              </>
            )}
          </button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-red-700 dark:text-red-300">{message}</p>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-red-600/70 dark:text-red-400/70">
              Type: <span className="font-mono">{type}</span>
            </span>
            {retryable !== undefined && (
              <span className="text-red-600/70 dark:text-red-400/70">
                Retryable: {retryable ? 'Yes' : 'No'}
              </span>
            )}
          </div>
          {stackTrace && (
            <div className="pt-2">
              <button
                onClick={() => setShowStackTrace(!showStackTrace)}
                className="flex items-center gap-1 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
              >
                {showStackTrace ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
                Stack Trace
              </button>
              {showStackTrace && (
                <pre className="mt-2 p-3 text-xs font-mono bg-red-100 dark:bg-red-950/50 rounded border border-red-200 dark:border-red-900/50 overflow-x-auto whitespace-pre-wrap text-red-800 dark:text-red-200">
                  {stackTrace}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
